import { Typography } from '@/components'
import { Column, Row } from '@/components/styled'
import { Breakpoint, CategoryId, ThemeColor } from '@/enums'
import { paths } from '@/routes/paths'
import { HomeRecommendation } from '@/types/location'
import { useGoToPath, useIsMobile } from '@/utils'
import { Button, Carousel } from '@surein/ui'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  CategoryCardConfig,
  CategoryCardConfigItem,
  isRecommendationNew,
} from '../helpers'
import {
  CarouselSection,
  CarouselSectionHeader,
  CarouselWrapper,
  MobileCoverageButtonContainer,
} from '../styles'
import { ContentCard, ViewAllCard } from './MissingCoverageCard'
import {
  RecommendationCard,
  RecommendationCardSkeleton,
} from './RecommendationCard'

export interface CategoryCardConfig {
  samplePrice: number
  image: {
    imageUrl: string
    bottom?: string
    right?: string
    aspectRatio?: string
    width?: string
    height?: string
  }
}

interface MakeCardsProps {
  locationId: string
  missingCategories: CategoryId[]
  recommendations: HomeRecommendation[]
}

// This function may seem esoteric but this is needed for the carousel to recognize
// the cards as different elements. Otherwise, the carousel will show them stacked
// if we try to map them separately.
const makeCards = ({
  locationId,
  missingCategories,
  recommendations,
}: MakeCardsProps) => {
  const cards: React.ReactNode[] = []

  recommendations.forEach((recommendation, index) => {
    cards.push(
      <RecommendationCard
        locationId={locationId}
        key={`recommendation-${recommendation.recommendationId}-${index}`}
        recommendationId={recommendation.recommendationId}
        categoryId={recommendation.categoryId}
        paymentPeriod={recommendation?.searchCriteria?.paymentPeriod || null}
        lowestPrice={recommendation.lowestPrice || 0}
        carriers={recommendation.carriers}
        isNew={isRecommendationNew(recommendation.createdAt)}
        sended={!!recommendation.sended}
      />,
    )
  })

  missingCategories.forEach((categoryId) => {
    cards.push(
      <ContentCard
        key={`missing-${categoryId}`}
        locationId={locationId}
        categoryId={categoryId}
        categoryCardConfig={
          (CategoryCardConfig[
            categoryId as keyof typeof CategoryCardConfig
          ] as CategoryCardConfigItem) ||
          (CategoryCardConfig.default as CategoryCardConfigItem)
        }
        paymentPeriod="monthly"
      />,
    )
  })

  cards.push(<ViewAllCard locationId={locationId} key="view-all-card" />)

  return cards
}

const makeLoadingCards = () => {
  return Array.from({ length: 3 }).map((_, index) => (
    <RecommendationCardSkeleton key={index} />
  ))
}

interface MissingCoverageSectionProps {
  locationId: string
  missingCategories: CategoryId[]
  recommendations: HomeRecommendation[]
  isLoading: boolean
}

export const MissingCoverageSection = ({
  isLoading,
  locationId,
  missingCategories = [],
  recommendations = [],
}: MissingCoverageSectionProps) => {
  const { t } = useTranslation()
  const { isMobile: isLgBreakpoint } = useIsMobile(Breakpoint.lg)
  const { isMobile: isMdBreakpoint } = useIsMobile(Breakpoint.md)
  const goToFindInsurances = useGoToPath(
    paths.findInsurancesWithKey(locationId),
  )

  const carouselCards = isLoading
    ? makeLoadingCards()
    : makeCards({
        locationId,
        missingCategories,
        recommendations,
      })

  const isOnlyViewAllCard = carouselCards.length === 1

  if (isOnlyViewAllCard) return null

  return (
    <CarouselSection>
      <CarouselSectionHeader>
        <Row gap="16px" justifyContent="space-between">
          <Column gap="4px" flex={1}>
            <Typography variant="h4" color={ThemeColor.b0}>
              {t('locationPage.missingCoverage')}
            </Typography>
            <Typography variant="p1Body" color={ThemeColor.b0}>
              {t('locationPage.mustHaveCoverage')}
            </Typography>
          </Column>
          {!isLgBreakpoint && (
            <Button
              variant="link"
              size="xs"
              className="text-white opacity-50"
              suffixIcon={ChevronRight}
              onClick={() => goToFindInsurances()}
            >
              <Typography variant="p1Body" color={ThemeColor.b0}>
                {t('locationPage.viewAllCoverage')}
              </Typography>
            </Button>
          )}
        </Row>
      </CarouselSectionHeader>
      <CarouselWrapper>
        <Carousel
          slidesInView={isLgBreakpoint ? 1 : 2}
          withArrows={!isMdBreakpoint}
        >
          {carouselCards}
        </Carousel>
      </CarouselWrapper>
      <MobileCoverageButtonContainer>
        {isLgBreakpoint && (
          <Button
            variant="link"
            size="xs"
            className="text-white opacity-50"
            suffixIcon={ChevronRight}
            onClick={() => goToFindInsurances()}
          >
            <Typography variant="p1Body" color={ThemeColor.b0}>
              {t('locationPage.viewAllCoverage')}
            </Typography>
          </Button>
        )}
      </MobileCoverageButtonContainer>
    </CarouselSection>
  )
}
